/**
 * PersonSubgraph Component
 * Renders a person-centered bipartite graph on the user profile page, showing
 * a single person node ("Me") connected to the organizations they are
 * affiliated with. Data comes from GET /api/graph/person/:userId/subgraph.
 *
 * Graph structure:
 * - Nodes: One "user" node (the person) and zero or more "organization" nodes.
 *   The user node uses the brand color (#1A3A5C) and a fixed size of 12px.
 *   Organization nodes are colored by sector via getNodeColor and sized at 8px.
 * - Links: Person-to-organization affiliation edges, each carrying a
 *   relationship label (owner, admin, professional, alumni, volunteer,
 *   collaborator, interested, connected). Links are drawn in a color matching
 *   the relationship type via the RELATIONSHIP_COLORS map.
 *
 * Data transformation:
 * - The API returns { nodes, edges, centerName, centerId }. The edges array
 *   is remapped to { source, target, label } so react-force-graph-2d can
 *   consume them as "links".
 *
 * Force simulation:
 * - Uses react-force-graph-2d defaults (charge ~-30, link distance ~30,
 *   center at [0,0]). The graph is small (star topology), so defaults
 *   produce a clean radial layout naturally.
 * - cooldownTicks=100, then onEngineStop auto-frames with zoomToFit.
 *
 * Custom link rendering (linkCanvasObject):
 * - Each link is drawn as a colored line using RELATIONSHIP_COLORS.
 * - When zoomed in (globalScale > 1.2) or there are 5 or fewer links,
 *   a text label is rendered at the midpoint with a white background
 *   rectangle for readability.
 *
 * Key interactions:
 * - Click an organization node to navigate to its detail page; clicking
 *   the center "user" node is a no-op.
 * - Hover highlights a node with a dark border and shows its label.
 * - Graph/Table toggle switches to a table view listing organizations
 *   with relationship-type badges.
 * - Labels are always visible for the center node, on hover, when zoomed
 *   in, or when there are 6 or fewer total nodes.
 */

import React, { useRef, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, Link } from 'react-router-dom';
import ForceGraph2D from 'react-force-graph-2d';
import {
    Card,
    Spinner,
    Alert,
    ButtonGroup,
    Button,
    Table,
    Badge,
} from 'react-bootstrap';

import { useGraphApi } from '@/shared/services/graphApi';

import { getNodeColor } from '../utils/graphColors';

import '../Graph.css';

// Color map for link/edge rendering in the graph view and badge coloring in
// the table view. Each key is a relationship type stored on the affiliation
// edge in Neo4j (e.g., "professional", "alumni"). Links are drawn using
// these colors in the custom linkCanvasObject renderer.
const RELATIONSHIP_COLORS = {
    owner: '#1565C0',
    admin: '#1976D2',
    professional: '#2E7D32',
    alumni: '#7B1FA2',
    volunteer: '#00838F',
    collaborator: '#F57C00',
    interested: '#FFA000',
    connected: '#9E9E9E',
};

// Badge variants for table view
const RELATIONSHIP_VARIANTS = {
    owner: 'primary',
    admin: 'info',
    professional: 'success',
    alumni: 'secondary',
    volunteer: 'info',
    collaborator: 'warning',
    interested: 'warning',
};

export default function PersonSubgraph({ userId, height = 300 }) {
    const navigate = useNavigate();
    const graphApi = useGraphApi();
    const graphRef = useRef();
    const containerRef = useRef();
    const [dimensions, setDimensions] = useState({ width: 400, height });
    const [hoveredNode, setHoveredNode] = useState(null);
    const [viewMode, setViewMode] = useState('graph');
    const [graphData, setGraphData] = useState({ nodes: [], links: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // --- Data fetching and transformation ---
    // Calls GET /api/graph/person/:userId/subgraph which returns the user
    // node plus all organizations they are affiliated with, along with
    // edges carrying the relationship type label.
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await graphApi.fetchUserGraph(userId);

                // Transform API response for react-force-graph-2d:
                // The library expects a "links" array (not "edges"), where
                // each link has { source, target }. We also carry "label"
                // (the relationship type) through for custom link rendering.
                setGraphData({
                    nodes: data.nodes || [],
                    links: (data.edges || []).map((e) => ({
                        source: e.source,
                        target: e.target,
                        label: e.label,
                    })),
                    centerName: data.centerName,
                    centerId: data.centerId,
                });
            } catch (err) {
                console.error('Failed to fetch user graph:', err);
                setError('Failed to load network connections');
            } finally {
                setLoading(false);
            }
        };

        if (userId) {
            fetchData();
        }
    }, [userId, graphApi]);

    // Handle responsive sizing
    useEffect(() => {
        const updateDimensions = () => {
            if (containerRef.current) {
                const width = containerRef.current.offsetWidth;
                setDimensions({ width, height });
            }
        };

        updateDimensions();
        window.addEventListener('resize', updateDimensions);
        return () => window.removeEventListener('resize', updateDimensions);
    }, [height]);

    // Handle node click - navigate to organization (not for "Me" node)
    const handleNodeClick = useCallback(
        (node) => {
            if (node.nodeType !== 'user') {
                navigate(`/organizations/${node.id}`);
            }
        },
        [navigate]
    );

    // --- Custom node rendering (Canvas 2D) ---
    // Two node types: "user" (the person, center of the star) and
    // "organization" (the affiliated orgs around the perimeter).
    const nodeCanvasObject = useCallback(
        (node, ctx, globalScale) => {
            const isCenter = node.nodeType === 'user';
            const isHovered = hoveredNode === node.id;

            // Fixed sizes (no connectionCount scaling here):
            // User node: 12px radius, org nodes: 8px radius.
            // The person node is larger to anchor the star layout visually.
            const baseSize = isCenter ? 12 : 8;
            const size = baseSize;

            // The center person node uses the RILE Connect brand color;
            // organization nodes are colored by their primarySector.
            let color;
            if (isCenter) {
                color = '#1A3A5C';
            } else {
                color = getNodeColor(node, 'sector');
            }

            // Draw filled circle
            ctx.beginPath();
            ctx.arc(node.x, node.y, size, 0, 2 * Math.PI);
            ctx.fillStyle = color;
            ctx.fill();

            // Center node: permanent gold ring; hovered org: dark border
            if (isCenter) {
                ctx.strokeStyle = '#FFD700';
                ctx.lineWidth = 3;
                ctx.stroke();
            } else if (isHovered) {
                ctx.strokeStyle = '#333';
                ctx.lineWidth = 2;
                ctx.stroke();
            }

            // Label rendering: center node and hovered nodes always show
            // labels. When the graph has 6 or fewer nodes, all labels are
            // shown since there is enough room. The center node uses a
            // larger bold font to reinforce its focal role.
            const label = node.name;
            const fontSize = isCenter
                ? Math.max(14 / globalScale, 6)
                : Math.max(12 / globalScale, 4);

            if (
                isCenter ||
                isHovered ||
                globalScale > 1.5 ||
                graphData.nodes.length <= 6
            ) {
                ctx.font = `${isCenter ? 'bold ' : ''}${fontSize}px Sans-Serif`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillStyle = '#333';

                // Truncate labels that exceed maxWidth. Uses a wider
                // threshold (180px) than OrganizationSubgraph (100px)
                // because person subgraphs have fewer nodes and more room.
                const maxWidth = 180 / globalScale;
                let displayLabel = label;
                if (ctx.measureText(label).width > maxWidth) {
                    while (
                        ctx.measureText(displayLabel + '...').width >
                            maxWidth &&
                        displayLabel.length > 0
                    ) {
                        displayLabel = displayLabel.slice(0, -1);
                    }
                    displayLabel += '...';
                }

                ctx.fillText(displayLabel, node.x, node.y + size + fontSize);
            }
        },
        [hoveredNode, graphData.nodes.length]
    );

    // --- Custom link/edge rendering (Canvas 2D) ---
    // Draws each link as a colored line with an optional text label at
    // the midpoint. The color encodes the relationship type (e.g., green
    // for "professional", purple for "alumni") using RELATIONSHIP_COLORS.
    const linkCanvasObject = useCallback(
        (link, ctx, globalScale) => {
            const start = link.source;
            const end = link.target;

            // After d3-force resolves link endpoints, source/target become
            // objects with x/y. Before resolution they are strings; bail out.
            if (typeof start !== 'object' || typeof end !== 'object') return;

            // Draw the line between the two node positions
            ctx.beginPath();
            ctx.moveTo(start.x, start.y);
            ctx.lineTo(end.x, end.y);
            ctx.strokeStyle = RELATIONSHIP_COLORS[link.label] || '#BDBDBD';
            ctx.lineWidth = 1.5;
            ctx.stroke();

            // Draw the relationship type label at the midpoint of the link.
            // Only shown when zoomed in past 1.2x or when there are 5 or
            // fewer links, to avoid label clutter on busy graphs.
            if (
                link.label &&
                (globalScale > 1.2 || graphData.links.length <= 5)
            ) {
                const midX = (start.x + end.x) / 2;
                const midY = (start.y + end.y) / 2;

                const fontSize = Math.max(8 / globalScale, 3);
                ctx.font = `${fontSize}px Sans-Serif`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';

                // Semi-transparent white background rectangle behind the
                // label text so it remains readable over the link line.
                const textWidth = ctx.measureText(link.label).width;
                ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
                ctx.fillRect(
                    midX - textWidth / 2 - 2,
                    midY - fontSize / 2 - 1,
                    textWidth + 4,
                    fontSize + 2
                );

                // Draw the label text in the relationship's color
                ctx.fillStyle = RELATIONSHIP_COLORS[link.label] || '#666';
                ctx.fillText(link.label, midX, midY);
            }
        },
        [graphData.links.length]
    );

    // Node hover handling
    const handleNodeHover = useCallback((node) => {
        setHoveredNode(node ? node.id : null);
    }, []);

    // Loading state
    if (loading) {
        return (
            <Card className="border-0 shadow-sm mt-4">
                <Card.Body className="p-4 text-center person-subgraph-loading">
                    <Spinner animation="border" variant="primary" size="sm" />
                    <p className="mt-2 mb-0 small text-muted">
                        Loading network...
                    </p>
                </Card.Body>
            </Card>
        );
    }

    // Error state
    if (error) {
        return (
            <Alert variant="warning" className="mt-4 mb-0">
                <small>{error}</small>
            </Alert>
        );
    }

    // No connections (only "Me" node or empty)
    const orgNodes = graphData.nodes.filter((n) => n.nodeType !== 'user');
    if (orgNodes.length === 0) {
        return (
            <Card className="border-0 shadow-sm mt-4">
                <Card.Body className="p-4 text-center text-muted">
                    <div className="person-subgraph-empty-icon">🔗</div>
                    <small>No organization connections yet.</small>
                </Card.Body>
            </Card>
        );
    }

    const connectionCount = orgNodes.length;

    return (
        <Card className="border-0 shadow-sm mt-4 person-subgraph">
            <Card.Header className="bg-white py-2 px-3">
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h6 className="mb-0 text-primary">My Network</h6>
                        <small className="text-muted">
                            {connectionCount} organization
                            {connectionCount !== 1 ? 's' : ''}
                        </small>
                    </div>
                    <ButtonGroup size="sm">
                        <Button
                            variant={
                                viewMode === 'graph'
                                    ? 'primary'
                                    : 'outline-primary'
                            }
                            onClick={() => setViewMode('graph')}
                            title="Graph view"
                        >
                            Graph
                        </Button>
                        <Button
                            variant={
                                viewMode === 'table'
                                    ? 'primary'
                                    : 'outline-primary'
                            }
                            onClick={() => setViewMode('table')}
                            title="Table view"
                        >
                            Table
                        </Button>
                    </ButtonGroup>
                </div>
            </Card.Header>

            {viewMode === 'graph' ? (
                <>
                    <Card.Body className="p-0" ref={containerRef}>
                        {/* ForceGraph2D for the person-centered star graph.
                            - linkCanvasObject: custom link renderer that
                              draws colored, labeled edges (see above).
                            - linkDirectionalParticles=0: no animated particles
                              along edges (kept simple for this small graph).
                            - Default d3-force config is sufficient here: the
                              star topology (one center, N leaves) lays out
                              naturally with the default charge and link forces.
                            - onEngineStop: auto-fits with 40px padding. */}
                        <ForceGraph2D
                            ref={graphRef}
                            graphData={graphData}
                            width={dimensions.width}
                            height={height}
                            nodeCanvasObject={nodeCanvasObject}
                            nodePointerAreaPaint={(node, color, ctx) => {
                                const isCenter = node.nodeType === 'user';
                                const size = isCenter ? 12 : 8;
                                ctx.fillStyle = color;
                                ctx.beginPath();
                                ctx.arc(
                                    node.x,
                                    node.y,
                                    size + 2,
                                    0,
                                    2 * Math.PI
                                );
                                ctx.fill();
                            }}
                            linkCanvasObject={linkCanvasObject}
                            linkDirectionalParticles={0}
                            onNodeClick={handleNodeClick}
                            onNodeHover={handleNodeHover}
                            cooldownTicks={100}
                            onEngineStop={() => {
                                if (graphRef.current) {
                                    graphRef.current.zoomToFit(300, 40);
                                }
                            }}
                            enableNodeDrag={true}
                            enableZoomInteraction={true}
                            enablePanInteraction={true}
                        />
                    </Card.Body>
                    <Card.Footer className="bg-white border-top-0 py-2 text-center">
                        <small className="text-muted">
                            Click on an organization to view details
                        </small>
                    </Card.Footer>
                </>
            ) : (
                <Card.Body className="p-0 person-subgraph-table">
                    <Table hover size="sm" className="mb-0">
                        <thead className="bg-light sticky-top">
                            <tr>
                                <th className="ps-3">Organization</th>
                                <th>Relationship</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orgNodes
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map((org) => (
                                    <tr key={org.id}>
                                        <td className="ps-3">
                                            <Link
                                                to={`/organizations/${org.id}`}
                                                className="text-decoration-none"
                                            >
                                                {org.name}
                                            </Link>
                                        </td>
                                        <td>
                                            <Badge
                                                bg={
                                                    RELATIONSHIP_VARIANTS[
                                                        org.relationshipType
                                                    ] || 'secondary'
                                                }
                                            >
                                                {org.relationshipType}
                                            </Badge>
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </Table>
                </Card.Body>
            )}
        </Card>
    );
}

PersonSubgraph.propTypes = {
    userId: PropTypes.string.isRequired,
    height: PropTypes.number,
};

PersonSubgraph.defaultProps = {
    height: 300,
};
